import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { optImg } from "../../lib/image";

const inputCls = "w-full rounded-lg border border-input bg-white px-3 py-2 text-sm outline-none focus:border-pine focus:ring-2 focus:ring-pine/15";
const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500";

const Card = ({ title, hint, children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-soft sm:p-6">
    <h3 className="font-display text-lg font-semibold text-pine">{title}</h3>
    {hint && <p className="mb-4 mt-0.5 text-xs text-gray-400">{hint}</p>}
    <div className="mt-4 space-y-4">{children}</div>
  </div>
);

const AdminContent = ({ onSaved }) => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!content) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-pine" /></div>;

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const setSetting = (k, v) => setContent((c) => ({ ...c, settings: { ...c.settings, [k]: v } }));
  const setListItem = (key, i, field, value) =>
    setContent((c) => ({ ...c, [key]: c[key].map((item, idx) => (idx === i ? { ...item, [field]: value } : item)) }));
  const addListItem = (key, blank) => setContent((c) => ({ ...c, [key]: [...c[key], blank] }));
  const removeListItem = (key, i) => setContent((c) => ({ ...c, [key]: c[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Website content saved — live immediately");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-content">
      <div className="flex items-center justify-between rounded-2xl bg-pine p-5 text-white">
        <div>
          <p className="font-display text-lg font-semibold">Edit your website</p>
          <p className="text-xs text-white/70">Every change goes live on the website as soon as you save.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="content-save-button"
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-pine transition-transform hover:scale-[1.03] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all changes
        </button>
      </div>

      <Card title="Homepage hero" hint="The big headline and photo visitors see first.">
        <div>
          <label className={labelCls}>Headline</label>
          <input className={inputCls} value={content.hero_title} onChange={(e) => set("hero_title", e.target.value)} data-testid="content-hero-title" />
        </div>
        <div>
          <label className={labelCls}>Subtitle</label>
          <textarea rows={2} className={inputCls} value={content.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} data-testid="content-hero-subtitle" />
        </div>
        <div>
          <label className={labelCls}>Hero image URL</label>
          <input className={inputCls} value={content.hero_image} onChange={(e) => set("hero_image", e.target.value)} data-testid="content-hero-image" />
          {content.hero_image && <img src={optImg(content.hero_image, 400)} alt="Hero preview" className="mt-2 h-28 w-52 rounded-lg object-cover" />}
        </div>
      </Card>

      <Card title="About / Why us section">
        <div>
          <label className={labelCls}>Section title</label>
          <input className={inputCls} value={content.about_title} onChange={(e) => set("about_title", e.target.value)} data-testid="content-about-title" />
        </div>
        <div>
          <label className={labelCls}>Intro text</label>
          <textarea rows={3} className={inputCls} value={content.about_text} onChange={(e) => set("about_text", e.target.value)} data-testid="content-about-text" />
        </div>
        <div>
          <label className={labelCls}>Reason cards (icon, title, text)</label>
          <div className="space-y-3">
            {content.why_us.map((item, i) => (
              <div key={i} className="rounded-xl border border-gray-100 p-3" data-testid={`why-us-item-${i}`}>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input className={inputCls} value={item.title} placeholder="Title"
                         onChange={(e) => setListItem("why_us", i, "title", e.target.value)} data-testid={`why-us-title-${i}`} />
                  <input className={inputCls} value={item.icon} placeholder="Icon (MapPin, Users, Compass, Clock, Award, MessageCircle)"
                         onChange={(e) => setListItem("why_us", i, "icon", e.target.value)} data-testid={`why-us-icon-${i}`} />
                </div>
                <textarea rows={2} className={`${inputCls} mt-2`} value={item.text} placeholder="Text"
                          onChange={(e) => setListItem("why_us", i, "text", e.target.value)} data-testid={`why-us-text-${i}`} />
                <button onClick={() => removeListItem("why_us", i)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-600" data-testid={`why-us-remove-${i}`}>
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              </div>
            ))}
            <button onClick={() => addListItem("why_us", { icon: "Compass", title: "", text: "" })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-pine" data-testid="why-us-add">
              <Plus className="h-3.5 w-3.5" /> Add reason
            </button>
          </div>
        </div>
      </Card>

      <Card title="Personalized tours section">
        <div>
          <label className={labelCls}>Title</label>
          <input className={inputCls} value={content.custom_title} onChange={(e) => set("custom_title", e.target.value)} data-testid="content-custom-title" />
        </div>
        <div>
          <label className={labelCls}>Text</label>
          <textarea rows={3} className={inputCls} value={content.custom_text} onChange={(e) => set("custom_text", e.target.value)} data-testid="content-custom-text" />
        </div>
      </Card>

      <Card title="Popular destinations" hint="Shown on the homepage. Use image URLs you have rights to.">
        {content.destinations.map((d, i) => (
          <div key={i} className="rounded-xl border border-gray-100 p-3" data-testid={`destination-item-${i}`}>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input className={inputCls} value={d.name} placeholder="Name" onChange={(e) => setListItem("destinations", i, "name", e.target.value)} data-testid={`destination-name-${i}`} />
              <input className={inputCls} value={d.blurb} placeholder="Short line" onChange={(e) => setListItem("destinations", i, "blurb", e.target.value)} data-testid={`destination-blurb-${i}`} />
            </div>
            <input className={`${inputCls} mt-2`} value={d.image} placeholder="Image URL" onChange={(e) => setListItem("destinations", i, "image", e.target.value)} data-testid={`destination-image-${i}`} />
            <button onClick={() => removeListItem("destinations", i)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-600" data-testid={`destination-remove-${i}`}>
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          </div>
        ))}
        <button onClick={() => addListItem("destinations", { name: "", blurb: "", image: "" })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-pine" data-testid="destination-add">
          <Plus className="h-3.5 w-3.5" /> Add destination
        </button>
      </Card>

      <Card title="FAQ" hint="Questions and answers shown on the homepage and FAQ page.">
        {content.faqs.map((f, i) => (
          <div key={i} className="rounded-xl border border-gray-100 p-3" data-testid={`faq-item-${i}`}>
            <input className={inputCls} value={f.q} placeholder="Question" onChange={(e) => setListItem("faqs", i, "q", e.target.value)} data-testid={`faq-q-${i}`} />
            <textarea rows={2} className={`${inputCls} mt-2`} value={f.a} placeholder="Answer" onChange={(e) => setListItem("faqs", i, "a", e.target.value)} data-testid={`faq-a-${i}`} />
            <button onClick={() => removeListItem("faqs", i)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-600" data-testid={`faq-remove-${i}`}>
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          </div>
        ))}
        <button onClick={() => addListItem("faqs", { q: "", a: "" })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-pine" data-testid="faq-add">
          <Plus className="h-3.5 w-3.5" /> Add question
        </button>
      </Card>

      <Card title="Gallery" hint="One image URL per line — shown on the homepage and the Gallery page.">
        <textarea rows={6} className={inputCls} value={content.gallery.join("\n")}
                  onChange={(e) => set("gallery", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                  data-testid="content-gallery" />
        <div className="flex flex-wrap gap-2">
          {content.gallery.slice(0, 8).map((src) => (
            <img key={src} src={optImg(src, 200)} alt="Gallery preview" loading="lazy" className="h-14 w-14 rounded-lg object-cover" />
          ))}
        </div>
      </Card>

      <Card title="Privacy Policy" hint="Editable template. Complete the marked [TODO] fields before publication.">
        <textarea rows={12} className={inputCls} value={content.privacy_text || ""}
                  placeholder="Leave empty to use the default template"
                  onChange={(e) => set("privacy_text", e.target.value)} data-testid="content-privacy" />
      </Card>

      <Card title="Contact & settings" hint="These power every WhatsApp button and contact link on the site.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>WhatsApp number (international, digits only)</label>
            <input className={inputCls} value={content.settings.whatsapp} onChange={(e) => setSetting("whatsapp", e.target.value)} data-testid="settings-whatsapp" />
          </div>
          <div>
            <label className={labelCls}>Contact email (shown on the site)</label>
            <input className={inputCls} value={content.settings.contact_email} onChange={(e) => setSetting("contact_email", e.target.value)} data-testid="settings-email" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Default WhatsApp pre-filled message</label>
          <textarea rows={2} className={inputCls} value={content.settings.whatsapp_message} onChange={(e) => setSetting("whatsapp_message", e.target.value)} data-testid="settings-wa-message" />
        </div>
        <div>
          <label className={labelCls}>Footer text</label>
          <input className={inputCls} value={content.settings.footer_text} onChange={(e) => setSetting("footer_text", e.target.value)} data-testid="settings-footer" />
        </div>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="content-save-button-bottom"
                className="inline-flex items-center gap-2 rounded-full bg-pine px-6 py-3 text-sm font-semibold text-white shadow-lift transition-transform hover:scale-[1.03] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all changes
        </button>
      </div>
    </div>
  );
};

export default AdminContent;
